import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import { useCart } from "@/store/cart";
import { ArrowLeft, Plus, Minus, Check } from "lucide-react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import { swayStyleFor } from "@/lib/swayStyle";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    api
      .get(`/products/${id}`)
      .then((r) => {
        // If backend followed a rename, swap the URL to the canonical slug.
        if (r.data?.redirect_from && r.data?.id && r.data.id !== id) {
          navigate(`/product/${r.data.id}`, { replace: true });
          return;
        }
        setProduct(r.data);
      })
      .catch(() => setNotFound(true));
  }, [id, navigate]);

  if (notFound) {
    return (
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-32 text-center" data-testid="product-not-found">
        <div className="font-display text-4xl text-white mb-4">Not the scent you're after.</div>
        <Link to="/shop" className="text-[#FF3D8B] uppercase text-sm tracking-widest">
          Back to Shop
        </Link>
      </div>
    );
  }

  if (!product) {
    return <div className="max-w-7xl mx-auto px-6 sm:px-10 py-32 text-white/50 text-sm">Loading…</div>;
  }

  const handleAdd = () => {
    addItem(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-10 py-12" data-testid={`product-page-${product.id}`}>
      <SEO
        title={`${product.name} — ${product.tagline}`}
        description={product.description}
        path={`/product/${product.id}`}
        image={product.image_url ? (product.image_url.startsWith("http") ? product.image_url : `https://bootybreeze.com${product.image_url}`) : undefined}
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": product.name,
          "description": product.description || product.tagline,
          "image": product.image_url && product.image_url.startsWith("http") ? product.image_url : `https://bootybreeze.com${product.image_url || ""}`,
          "sku": product.id,
          "brand": { "@type": "Brand", "name": "Booty Breeze" },
          "category": "Automotive > Car Air Fresheners",
          "additionalProperty": (product.notes || []).map((n) => ({
            "@type": "PropertyValue",
            "name": "Fragrance Note",
            "value": n,
          })),
          "offers": {
            "@type": "Offer",
            "url": `https://bootybreeze.com/product/${product.id}`,
            "priceCurrency": "USD",
            "price": product.price,
            "availability": "https://schema.org/InStock",
            "itemCondition": "https://schema.org/NewCondition",
            "shippingDetails": {
              "@type": "OfferShippingDetails",
              "shippingRate": { "@type": "MonetaryAmount", "value": 0, "currency": "USD" },
              "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "US" },
              "deliveryTime": {
                "@type": "ShippingDeliveryTime",
                "transitTime": { "@type": "QuantitativeValue", "minValue": 3, "maxValue": 5, "unitCode": "DAY" }
              }
            }
          }
        }}
      />
      <Link
        to="/shop"
        className="inline-flex items-center gap-2 text-white/50 hover:text-[#FF3D8B] text-xs uppercase tracking-widest mb-10 transition-colors"
      >
        <ArrowLeft size={14} /> All Scents
      </Link>

      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="relative aspect-square flex items-center justify-center overflow-visible"
        >
          <img
            src={resolveImage(product.image_url || product.image, 900, { removeBg: true })}
            alt={product.name}
            className="sway-hang w-4/5 drop-shadow-[0_0_60px_rgba(255,61,139,0.4)]"
            style={swayStyleFor(product.id)}
          />
          {product.badge && (
            <span className="absolute top-5 left-5 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-[#FF3D8B] text-black font-bold">
              {product.badge}
            </span>
          )}
        </motion.div>

        <div>
          <h1 className="font-display text-5xl sm:text-6xl text-white leading-none">{product.name}</h1>
          <p className="mt-4 text-[#A855F7] text-lg italic">{product.tagline}</p>
          <div className="mt-6 text-3xl font-bold text-white" data-testid="product-price">
            ${product.price.toFixed(2)}
          </div>
          <p className="mt-6 text-white/70 leading-relaxed text-base max-w-lg">
            {product.description}
          </p>

          {product.notes?.length > 0 && (
            <div className="mt-8">
              <div className="text-white/40 text-[11px] uppercase tracking-widest mb-3">
                Fragrance Notes
              </div>
              <div className="flex flex-wrap gap-2">
                {product.notes.map((n) => (
                  <span
                    key={n}
                    className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/80"
                  >
                    {n}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-10 flex items-center gap-4">
            <div className="flex items-center gap-3 border border-white/15 rounded-full px-2 py-1.5">
              <button
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="w-9 h-9 rounded-full text-white/80 hover:text-[#FF3D8B] flex items-center justify-center"
                data-testid="qty-decrease-btn"
              >
                <Minus size={14} />
              </button>
              <span className="text-white font-semibold w-6 text-center" data-testid="qty-value">
                {qty}
              </span>
              <button
                onClick={() => setQty(qty + 1)}
                className="w-9 h-9 rounded-full text-white/80 hover:text-[#FF3D8B] flex items-center justify-center"
                data-testid="qty-increase-btn"
              >
                <Plus size={14} />
              </button>
            </div>
            <button
              onClick={handleAdd}
              data-testid="add-to-cart-btn"
              className="flex-1 px-8 py-4 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#ff5ba0] transition-colors glow-pink"
            >
              {added ? (
                <span className="inline-flex items-center gap-2">
                  <Check size={16} /> Added
                </span>
              ) : (
                "Add to Cart"
              )}
            </button>
          </div>

          <div className="mt-8 pt-6 border-t border-white/10 grid grid-cols-2 gap-4 text-xs text-white/50">
            <div>
              <div className="text-white/80 font-semibold mb-1">Discreet Shipping</div>
              Plain packaging, always.
            </div>
            <div>
              <div className="text-white/80 font-semibold mb-1">Long-lasting</div>
              3–4 weeks of scent.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
